// @flow

import _ from 'lodash';
import moment from 'moment';
import xss from 'xss';

import { routes } from 'src/routes';
import { prepareDateLabelTooltipOnChart } from 'src/helpers/analytics-helpers';
import { formatNumberInThousand, formatNumberWithMoney, getAssetURL } from 'src/helpers';
import { formatMoney } from 'src/helpers/money-format';

export const NUMBER_DAYS_ON_TOTAL = 14;
export const NUMBER_ITEMS_ON_LIST = 50;
export const NUMBER_FILTER_OPTION_VALUES_PER_PAGE = 10;
export const NUMBER_SAVED_EXECUTION_ID_GROUP = 20;
export const NUMBER_DAYS_LIMIT = 365;

// For new analytics
export const TOP_ITEM_IN_OVERVIEW = 5;
export const TOP_ITEM_IN_OVERVIEW_RECOMMENDATION = 4;
export const TOP_ITEM_IN_DETAIL = 30;
export const NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL = 10;
export const MAX_NUM_X_AXIS_POINTS_RECOMMEND_CHART = 4;
export const MAX_NUM_X_AXIS_POINTS_COLLECTION_CHART = 5;
export const MAX_NUM_X_AXIS_POINTS_SEARCH_CHART = 5;
export const MAX_NUM_X_AXIS_POINTS_APP_IMPACT_CHART = 6;
const MAX_POINT_X_AXIS = 4; // For apex-chart

export const QUERY_EXECUTION_KEY = {
  TOTAL: 'get_total',
  TOTAL_ALL_SALES: 'get_total_all_sales',
  TOP_COLLECTIONS_TOTAL_SALES: 'get_top_collections_total_sales',
  TOTAL_SALES: 'get_total_sales',
  TOTAL_SALES_COMPARED: 'get_total_sales_compared',
  TOP_COLLECTIONS_TOTAL_FILTER_CLICKS: 'get_top_collections_total_filter_clicks',
  TOTAL_FILTER_CLICKS: 'get_total_filter_clicks',
  TOTAL_FILTER_CLICKS_COMPARED: 'get_total_filter_clicks_compared',
  TOP_FILTER_OPTION_COMBINATIONS: 'get_top_filter_option_combinations',
  TOP_FILTER_OPTION_COLLECTION_PAGE: 'get_top_filter_option_collection_page',
  TOTAL_SALES_ON_SEARCH: 'get_total_sales_on_search',
  TOTAL_SALES_ON_SEARCH_COMPARED: 'get_total_sales_on_search_compared',
  TOTAL_SEARCHES: 'get_total_searches',
  TOTAL_SEARCHES_COMPARED: 'get_total_searches_compared',
  TOP_SEARCH_TERMS: 'get_top_search_terms',
  TOP_SEARCH_TERMS_WITH_NO_RESULT: 'get_top_search_terms_with_no_result',
  TOTAL_FILTER_CLICKS_SEARCH_PAGE: 'get_total_filter_clicks_search_page',
  TOTAL_FILTER_CLICKS_SEARCH_PAGE_COMPARED: 'get_total_filter_clicks_search_page_compared',
  TOP_FILTER_OPTION_SEARCH_PAGE: 'get_top_filter_option_search_page',
  SEARCH_TERMS_WITHOUT_RESULT: 'get_search_terms_without_result',
  GET_TOTAL_SEARCH_REVENUE: 'get_total_search_revenue',
  GET_TOTAL_SEARCH_REVENUE_COMPARED: 'get_total_search_revenue_compared',
  SEARCHES_WITHOUT_RESULT_RATE: 'get_searches_without_result_rate',
  SEARCHES_WITHOUT_RESULT_RATE_COMPARED: 'get_searches_without_result_rate_compared',
  SEARCH_TERMS: 'get_searches_search_terms',
  SEARCH_TERM_DETAIL: 'get_searches_search_terms_detail',
  SEARCH_PRODUCT_PERFORMANCES: 'get_searches_product_performances',
  CONVERSION_RATE: 'get_conversion_rate',
  CONVERSION_RATE_COMPARED: 'get_conversion_rate_compared',
  SEARCHES_RATE: 'get_searches_rate',
  SEARCHES_RATE_COMPARED: 'get_searches_rate_compared',
  TOTAL_ACTIVITY: 'get_total_revenue',
  TOTAL_ACTIVITY_COMPARED: 'get_total_revenue_compared',
  TOTAL_REVENUE: 'get_total_revenue',
  TOTAL_REVENUE_COMPARED: 'get_total_revenue_compared',
  ORDER_COUNT: 'get_order_count',
  ORDER_COUNT_COMPARED: 'get_order_count_compared',
  AVERAGE_ORDER_VALUE: 'get_average_order_value',
  AVERAGE_ORDER_VALUE_COMPARED: 'get_average_order_value_compared',
  REVENUE_PER_VISITOR: 'get_revenue_per_visitor',
  REVENUE_PER_VISITOR_COMPARED: 'get_revenue_per_visitor_compared',
  TOTAL_SEARCHES_WITHOUT_CLICK: 'get_total_searches_without_click',
  TOTAL_SEARCHES_WITHOUT_CLICK_COMPARED: 'get_total_searches_without_click_compared',
  TOP_SEARCH_TERMS_WITHOUT_CLICK: 'get_top_search_terms_without_click',
  TOP_SEARCH_TERMS_WITH_CLICK: 'get_top_search_terms_with_click',

  COLLECTION_TOTAL_REVENUE_GENERATED: 'get_collection_total_revenue_generated',
  COLLECTION_TOTAL_REVENUE_GENERATED_COMPARED: 'get_collection_total_revenue_generated_compared',
  COLLECTION_CONVERSION_RATE: 'get_collection_conversion_rate',
  COLLECTION_CONVERSION_RATE_COMPARED: 'get_collection_conversion_rate_compared',
  COLLECTION_TOTAL_FILTER_CLICKS: 'get_collection_total_filter_clicks',
  COLLECTION_TOTAL_FILTER_CLICKS_COMPARED: 'get_collection_total_filter_clicks_compared',
  COLLECTION_TOP: 'get_collection_top',
  COLLECTION_TOP_COMPARED: 'get_collection_top_compared',
  COLLECTION_TOP_FILTER_OPTION_VALUE: 'get_collection_top_filter_option_value',
  COLLECTION_TOP_FILTER_OPTION_VALUE_COMPARED: 'get_collection_top_filter_option_value_compared',
  COLLECTION_TOP_FILTER_OPTION_VALUE_COMBINATION:
    'get_collection_top_filter_option_value_combination',
  COLLECTION_TOP_FILTER_OPTION_VALUE_COMBINATION_COMPARED:
    'get_collection_top_filter_option_value_combination_compared',

  RECOMMENDATION_TOTAL_REVENUE: 'get_recommendation_total_revenue',
  RECOMMENDATION_TOTAL_REVENUE_COMPARED: 'get_recommendation_total_revenue_compared',
  RECOMMENDATION_CONVERSION_RATE: 'get_recommendation_conversion_rate',
  RECOMMENDATION_CONVERSION_RATE_COMPARED: 'get_recommendation_conversion_rate_compared',
  RECOMMENDATION_CLICK_RATE: 'get_recommendation_click_rate',
  RECOMMENDATION_CLICK_RATE_COMPARED: 'get_recommendation_click_rate_compared',
  RECOMMENDATION_TOP_PAGE_PERFORMANCE: 'get_recommendation_top_page_performance',
  RECOMMENDATION_TOP_WIDGET_PERFORMANCE: 'get_recommendation_top_widget_performance',
  RECOMMENDATION_TOP_PRODUCT_PERFORMANCE: 'get_recommendation_top_product_performance',

  PRE_ORDER_TOP_PRODUCT_PERFORMANCE: 'get_pre_order_top_product_performance',

  TOTAL_AI_REVENUE: 'get_total_ai_revenue',
  TOTAL_AI_REVENUE_COMPARED: 'get_total_ai_revenue_compared'
};

export const DATE_RANGE = [
  { value: 'today', label: 'AdminHTML.Analytics.Constants.dateRange_Today', days: 1 },
  { value: 'yesterday', label: 'AdminHTML.Analytics.Constants.dateRange_Yesterday', days: 1 },
  { value: 'last7Days', label: 'AdminHTML.Analytics.Constants.dateRange_Last7days', days: 7 },
  { value: 'last30Days', label: 'AdminHTML.Analytics.Constants.dateRange_Last30days', days: 30 },
  { value: 'lastMonth', label: 'AdminHTML.Analytics.Constants.dateRange_Lastmonth', days: 31 },
  { value: 'last90Days', label: 'AdminHTML.Analytics.Constants.dateRange_Last90days', days: 90 },
  { value: 'last180Days', label: 'AdminHTML.Analytics.Constants.dateRange_Last180days', days: 180 },
  { value: 'last365Days', label: 'AdminHTML.Analytics.Constants.dateRange_Last365days', days: 365 },
  { value: 'custom', label: 'AdminHTML.Analytics.Constants.dateRange_Custom', days: 0 }
];

// Line chart options for primary data
export const PRIMARY_DATASET_LINE_OPTIONS = {
  label: 'Primary data',
  type: 'line',
  fill: false,
  borderColor: '#00A19F',
  backgroundColor: '#00A19F',
  pointBorderColor: '#00A19F',
  pointBackgroundColor: '#00A19F',
  pointHoverBackgroundColor: '#00A19F',
  pointHoverBorderColor: '#fff',
  pointHoverBorderWidth: 2,
  pointRadius: 0,
  borderWidth: 2,
  lineTension: 0,
  borderJoinStyle: 'round',
  spanGaps: false
};

// Line chart options for compared data
export const COMPARED_DATASET_LINE_OPTIONS = {
  label: 'Compared data',
  type: 'line',
  fill: false,
  borderColor: '#919EAB',
  backgroundColor: '#919EAB',
  pointBorderColor: '#919EAB',
  pointBackgroundColor: '#919EAB',
  pointHoverBackgroundColor: '#919EAB',
  pointHoverBorderColor: '#fff',
  pointHoverBorderWidth: 2,
  pointRadius: 0,
  borderWidth: 2,
  lineTension: 0,
  borderJoinStyle: 'round',
  spanGaps: false
};

// Options for chart type Mixed
export const CHART_OPTIONS = {
  title: {
    display: true,
    fontSize: 12,
    position: 'top'
  },
  legend: {
    display: true,
    position: 'bottom',
    align: 'end',
    labels: {
      lineCap: 'round',
      lineWidth: '12',
      boxWidth: 12
    }
  },
  hover: {
    mode: 'index',
    intersect: false
  },
  tooltips: {
    mode: 'index',
    position: 'nearest',
    intersect: false,
    bodySpacing: 7,
    callbacks: {
      title: tooltipItem => {
        return `${tooltipItem[0].xLabel}`;
      },
      label: tooltipItem => {
        return ` ${tooltipItem.yLabel}`;
      }
    }
  },
  scales: {
    xAxes: [
      {
        ticks: {
          padding: 10,
          autoSkip: true,
          autoSkipPadding: 150,
          maxRotation: 0,
          minRotation: 0
        },
        gridLines: {
          borderDash: [5, 5], // Draw dash
          zeroLineWidth: 0,
          drawTicks: false // Hide lines beside the ticks in the axis area beside the chart
        }
      }
    ],
    yAxes: [
      {
        ticks: {
          padding: 10,
          callback: function (label) {
            return label > 1000 ? label / 1000 + 'k' : label;
          }
        },
        gridLines: {
          drawBorder: false // Hide y axes
        }
      }
    ]
  }
};

// The shop's money_format is merchant-controlled and may carry markup such as
// <span class=money>. The admin has no styling for those tags, so stripping them
// keeps the rendered value identical while removing any script-bearing markup.
const stripMoneyMarkup = value => {
  const stripped = xss(String(value), {
    whiteList: {},
    stripIgnoreTag: true,
    stripIgnoreTagBody: ['script', 'style']
  });

  // Shopify's built-in formats carry HTML entities (&pound;, &euro;). The value
  // is rendered into a text node, so decode them here or they show up literally.
  // DOMParser only reads the parsed text back out - it runs no script and never
  // attaches the result to the live document.
  const decoded = new DOMParser().parseFromString(stripped, 'text/html');

  return decoded.documentElement.textContent || '';
};

/**
 * Render custom tooltip
 *
 * @param  {array} source         data source
 * @param  {array} comparedSource compared data
 *
 * @return {html element}         tooltip content
 */
export const customTooltip = (source, comparedSource, compare, isMoneyFormat = false) => {
  return {
    // Disable the on-canvas tooltip
    enabled: false,

    custom: function (tooltipModel) {
      // Tooltip Element
      var tooltipEl = document.getElementById('chartjs-tooltip');

      // Create element on first render
      if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.id = 'chartjs-tooltip';
        tooltipEl.appendChild(document.createElement('table'));
        document.body.appendChild(tooltipEl);
      }

      // Hide if no tooltip
      if (tooltipModel.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
      }

      // Set caret Position
      tooltipEl.classList.remove('above', 'below', 'no-transform');
      if (tooltipModel.yAlign) {
        tooltipEl.classList.add(tooltipModel.yAlign);
      } else {
        tooltipEl.classList.add('no-transform');
      }

      function getBody(bodyItem) {
        return bodyItem.lines;
      }

      // Set Text
      if (tooltipModel.body) {
        var titleLines = tooltipModel.title || [];

        // Get data point index
        const dataPointsIndex = _.get(tooltipModel, 'dataPoints[0].index', 0);

        // Tooltip content under table structure, built as nodes so that
        // merchant-controlled values cannot be parsed as HTML
        const thead = document.createElement('thead');
        const tbody = document.createElement('tbody');

        // bodyLines = [comparedData, data]
        // To allows the data is above compared data in tooltip, we should run loop from bottom to top
        const bodyLines = tooltipModel.body.map(getBody);
        const numberBodyLines = bodyLines.length;
        for (let i = numberBodyLines - 1; i >= 0; i--) {
          // Render title
          let titleContent = prepareDateLabelTooltipOnChart(_.get(titleLines, `[0]`, '')); // Default title;
          // Check data  = null and compared data != null, keep title of compared data
          if (compare) {
            const dataValue = _.get(source, `[${dataPointsIndex}].total`, null);
            titleContent =
              i !== 0 && dataValue !== null
                ? titleContent
                : prepareDateLabelTooltipOnChart(
                    _.get(comparedSource, `[${dataPointsIndex}].datetime`, '')
                  ); // Get title from compared source
          }

          // Color box style
          const colors = tooltipModel.labelColors[i];
          let styleBoxColor = 'background:' + colors.backgroundColor;
          styleBoxColor += '; border-color:' + colors.borderColor;
          styleBoxColor +=
            '; border-width: 2px; width: 15px; height: 100%; padding: 0 5px 0 5px; border-radius: 2px;';

          // Row content style
          // Add border bottom for the first row if there are 2 rows
          let styleValue = 'color: #fff; text-align: center';
          styleValue +=
            numberBodyLines > 1 && i !== 0
              ? '; padding-bottom: 0.5rem; border-bottom: 1px solid #fff'
              : '';

          const span = document.createElement('span');
          span.style.cssText = styleBoxColor;
          span.textContent = '\u00a0';

          const formatBodyLines = isMoneyFormat
            ? stripMoneyMarkup(formatNumberWithMoney(bodyLines[i]))
            : bodyLines[i];

          // Row content
          const titleCell = document.createElement('td');
          titleCell.style.cssText = 'color: #fff';
          titleCell.textContent = titleContent;
          const titleRow = document.createElement('tr');
          titleRow.appendChild(titleCell);
          tbody.appendChild(titleRow);

          const valueCell = document.createElement('td');
          valueCell.style.cssText = styleValue;
          valueCell.appendChild(span);
          valueCell.appendChild(document.createTextNode(formatBodyLines));
          const valueRow = document.createElement('tr');
          valueRow.appendChild(valueCell);
          tbody.appendChild(valueRow);
        }

        var tableRoot = tooltipEl.querySelector('table');
        while (tableRoot.firstChild) {
          tableRoot.removeChild(tableRoot.firstChild);
        }
        tableRoot.appendChild(thead);
        tableRoot.appendChild(tbody);
      }

      // `this` will be the overall tooltip
      var position = this._chart.canvas.getBoundingClientRect();

      // Display, position, and set styles for font
      tooltipEl.style.opacity = 0.9;
      tooltipEl.style.backgroundColor = '#212b36';
      tooltipEl.style.borderRadius = '3px';
      tooltipEl.style.position = 'absolute';
      tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
      tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
      tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
      tooltipEl.style.fontSize = tooltipModel.bodyFontSize + 'px';
      tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
      tooltipEl.style.padding = tooltipModel.yPadding + 'px ' + tooltipModel.xPadding + 'px';
      tooltipEl.style.pointerEvents = 'none';
    }
  };
};

export const ANALYTICS_TAB_ID = {
  OVERVIEW_SEARCH: 'overview-search-analytics',
  SEARCHES: 'searches-analytics',
  SEARCHES_WITH_CLICKS: 'searches-with-clicks-analytics',
  SEARCHES_WITHOUT_CLICKS: 'searches-without-clicks-analytics',
  SEARCHES_WITHOUT_RESULTS: 'searches-without-results-analytics',
  PRODUCT_PERFORMANCE: 'product-performance-analytics',
  OVERVIEW_COLLECTION: 'overview-collection-analytics',
  COLLECTION_PERFORMANCE: 'collection-performance',
  FILTER_OPTION_VALUE: 'filter-option-value',
  FILTER_OPTION_VALUE_COMBINATION: 'filter-option-value-combination',
  RECOMMENDATION_OVERVIEW: 'recommendation-overview',
  RECOMMENDATION_PAGE_PERFORMANCE: 'recommendation-page-peformance',
  RECOMMENDATION_WIDGET_PERFORMANCE: 'recommendation-widget-peformance',
  RECOMMENDATION_PRODUCT_PERFORMANCE: 'recommendation-product-peformance',
  PRE_ORDER_PRODUCT: 'pre-order-analytics-product',
  PRE_ORDERS: 'pre-orders'
};

export const SEARCH_ANALYTICS_TABS = [
  {
    id: ANALYTICS_TAB_ID.OVERVIEW_SEARCH,
    content: 'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_Overview',
    panelID: ANALYTICS_TAB_ID.OVERVIEW_SEARCH,
    link: routes.analytics.overviewSearch()
  },
  {
    id: ANALYTICS_TAB_ID.SEARCHES,
    content: 'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_Searches',
    panelID: ANALYTICS_TAB_ID.SEARCHES,
    link: routes.analytics.searches()
  },
  {
    id: ANALYTICS_TAB_ID.SEARCHES_WITH_CLICKS,
    content: 'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_SearchesWithClicks',
    panelID: ANALYTICS_TAB_ID.SEARCHES,
    link: routes.analytics.searchesWithClick()
  },
  {
    id: ANALYTICS_TAB_ID.SEARCHES_WITHOUT_CLICKS,
    content: 'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_SearchesWithoutClicks',
    panelID: ANALYTICS_TAB_ID.SEARCHES_WITHOUT_CLICKS,
    link: routes.analytics.searchesWithoutClicks()
  },
  {
    id: ANALYTICS_TAB_ID.SEARCHES_WITHOUT_RESULTS,
    content: 'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_SearchesWithoutResults',
    panelID: ANALYTICS_TAB_ID.SEARCHES_WITHOUT_RESULTS,
    link: routes.analytics.searchesWithoutResults()
  },
  {
    id: ANALYTICS_TAB_ID.PRODUCT_PERFORMANCE,
    content: 'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_ProductPerformance',
    panelID: ANALYTICS_TAB_ID.PRODUCT_PERFORMANCE,
    link: routes.analytics.productPerformance()
  }
];

export const COLLECTION_ANALYTICS_TABS = [
  {
    id: ANALYTICS_TAB_ID.OVERVIEW_COLLECTION,
    content: 'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_Overview',
    panelID: ANALYTICS_TAB_ID.OVERVIEW_COLLECTION,
    link: routes.analytics.overviewCollection()
  },
  {
    id: ANALYTICS_TAB_ID.COLLECTION_PERFORMANCE,
    content: 'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_CollectionPerformance',
    panelID: ANALYTICS_TAB_ID.COLLECTION_PERFORMANCE,
    link: routes.analytics.collectionPerformance()
  },
  {
    id: ANALYTICS_TAB_ID.FILTER_OPTION_VALUE,
    content: 'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_FilterOptionValue',
    panelID: ANALYTICS_TAB_ID.FILTER_OPTION_VALUE,
    link: routes.analytics.filterOptionValue()
  },
  {
    id: ANALYTICS_TAB_ID.FILTER_OPTION_VALUE_COMBINATION,
    content:
      'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_FilterOptionValueCombination',
    panelID: ANALYTICS_TAB_ID.FILTER_OPTION_VALUE_COMBINATION,
    link: routes.analytics.filterOptionValueCombination()
  }
];

export const RECOMMENDATION_ANALYTICS_TABS = [
  {
    id: ANALYTICS_TAB_ID.OVERVIEW_CRECOMMENDATION_OVERVIEWOLLECTION,
    content: 'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_Overview',
    panelID: ANALYTICS_TAB_ID.RECOMMENDATION_OVERVIEW,
    link: routes.analytics.recommendationOverview()
  },
  {
    id: ANALYTICS_TAB_ID.RECOMMENDATION_PAGE_PERFORMANCE,
    content:
      'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_Recommendation_Page_Performance',
    panelID: ANALYTICS_TAB_ID.RECOMMENDATION_PAGE_PERFORMANCE,
    link: routes.analytics.recommendationPagePerformance()
  },
  {
    id: ANALYTICS_TAB_ID.RECOMMENDATION_WIDGET_PERFORMANCE,
    content:
      'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_Recommendation_Widget_Performance',
    panelID: ANALYTICS_TAB_ID.RECOMMENDATION_WIDGET_PERFORMANCE,
    link: routes.analytics.recommendationWidgetPerformance()
  },
  {
    id: ANALYTICS_TAB_ID.RECOMMENDATION_PRODUCT_PERFORMANCE,
    content:
      'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_Recommendation_Product_Performance',
    panelID: ANALYTICS_TAB_ID.RECOMMENDATION_PRODUCT_PERFORMANCE,
    link: routes.analytics.recommendationProductPerformance()
  }
];

export const PRE_ORDER_ANALYTICS_TABS = [
  {
    id: ANALYTICS_TAB_ID.PRE_ORDERS,
    content: 'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_PreOrders',
    panelID: ANALYTICS_TAB_ID.PRE_ORDERS,
    link: routes.analytics.preOrders()
  },
  {
    id: ANALYTICS_TAB_ID.PRE_ORDER_PRODUCT,
    content: 'AdminHTML.Analytics.AnalyticsV2Tabs.analyticsTabs_TabContent_PreOrder_Product',
    panelID: ANALYTICS_TAB_ID.PRE_ORDER_PRODUCT,
    link: routes.analytics.preOrderProduct()
  }
];

/**
 * Get options for line chart
 *
 * @param  {array} categories     data source
 * @param  {boolean} isCompare
 * @param  {number} max           max value of Y-axis chart
 * @param  {string} suffixOfValue default %
 * @param  {number} yAxisTickAmount    amount of milestones of chart (Y-axis)
 *
 * @return {any}                  options for line chart
 */
export function getDatasetLineOptionsV2(
  categories,
  isCompare = false,
  yAxisMax,
  suffixOfValue = '',
  yAxisTickAmount = 4,
  moneyFormat,
  isLimitxAxis,
  colors = ['#11BC92', '#E0E3E7']
) {
  const isPercent = suffixOfValue === '%';
  const firstDateTime = categories[0];
  const lastDateTime = [...categories].reverse().find(i => i !== ''); // categories.at(-1);
  const diffHours =
    firstDateTime && lastDateTime
      ? moment(lastDateTime).diff(moment(firstDateTime), 'hours') + 1
      : undefined;

  const options = {
    chart: {
      width: '100%',
      type: 'line',
      toolbar: {
        show: false,
        autoSelected: 'pan'
      }
    },
    colors,
    stroke: {
      width: 3
    },
    xaxis: {
      categories,
      tickAmount: isLimitxAxis
        ? undefined
        : diffHours <= 48
        ? MAX_POINT_X_AXIS + 1
        : MAX_POINT_X_AXIS,
      labels: {
        style: {
          colors: [],
          fontSize: '12px',
          fontWeight: 500
        },
        rotate: 0,
        formatter: function (value) {
          if (!value || _.isEmpty(categories)) {
            return '';
          }
          if (diffHours <= 48) {
            return moment(value).format('h A');
          }

          return moment(value).format('MMM DD');
        }
      }
    },
    yaxis: {
      labels: {
        formatter: value => {
          let displayed = isPercent
            ? value.toFixed(0)
            : formatNumberInThousand(Math.round(value), true);
          // if (!isPercent && moneyFormat) {
          //   displayed = formatMoney(value, moneyFormat);
          // }
          return `${displayed}${suffixOfValue}`;
        },
        style: {
          colors: [],
          fontSize: '12px',
          fontWeight: 500
        }
      },
      min: 0,
      tickAmount: yAxisTickAmount
    },
    tooltip: {
      custom: function ({ series, dataPointIndex, w }) {
        const xAxisDatetime = moment(w.config.series[0].fullCategories[dataPointIndex]);
        let displayedSource = isPercent
          ? series[0][dataPointIndex].toFixed(2)
          : formatNumberInThousand(series[0][dataPointIndex], true);
        if (!isPercent && moneyFormat) {
          displayedSource = formatMoney(series[0][dataPointIndex], moneyFormat);
        }

        let displayedCompared = '';
        if (isCompare) {
          displayedCompared = isPercent
            ? series[1][dataPointIndex].toFixed(2)
            : formatNumberInThousand(series[1][dataPointIndex], true);
        }
        if (!isPercent && moneyFormat && isCompare) {
          displayedCompared = formatMoney(series[1][dataPointIndex], moneyFormat);
        }

        return `<div class="custom-tooltip">
            <div class="custom-tooltip-title">${xAxisDatetime.format(
              `MMM DD yyyy${diffHours <= 48 ? ', hh A' : ''}`
            )}</div>
            <div class="custom-tooltip-series-group">
              <span
                class="custom-tooltip-marker"
                style="background-color:${w.config.colors[0]}">
              </span>

              <div class="custom-tooltip-text">
                <div class="custom-tooltip-y-group">
                  <span class="custom-tooltip-text-y-label">
                    ${w.config.series[0].name}
                  </span>

                  <span class="custom-tooltip-text-y-value">
                    ${displayedSource}${suffixOfValue}
                  </span>
                </div>
              </div>
            </div>
            ${
              isCompare
                ? `
                  <div class="custom-tooltip-series-group">
                    <span
                      class="custom-tooltip-marker"
                      style="background-color:${w.config.colors[1]}">
                    </span>

                    <div class="custom-tooltip-text">
                      <div class="custom-tooltip-y-group">
                        <span class="custom-tooltip-text-y-label">
                          ${w.config.series[1].name}
                        </span>

                        <span class="custom-tooltip-text-y-value">
                          ${displayedCompared}${suffixOfValue}
                        </span>
                      </div>
                    </div>
                  </div>
                `
                : ''
            }
          </div>`;
      }
    }
  };

  if (yAxisMax) {
    options.yaxis.max = yAxisMax;
  }

  if (isCompare) {
    options.legend = {
      show: true,
      position: 'top',
      horizontalAlign: 'right',
      onItemClick: {
        toggleDataSeries: false
      }
    };
  }

  return options;
}

/**
 * Get options for line chart
 *
 * @param  {array} categories     data source
 * @param  {boolean} isCompare
 * @param  {number} max           max value of Y-axis chart
 * @param  {string} suffixOfValue default %
 * @param  {number} yAxisTickAmount    amount of milestones of chart (Y-axis)
 *
 * @return {any}                  options for line chart
 */
export function getDatasetBarOptionsV2(categories, isCompare) {
  const options = {
    chart: {
      width: '100%',
      type: 'bar',
      toolbar: { show: false, autoSelected: 'pan' } // Hide toolbar
    },
    colors: ['#11BC92', '#E0E3E7'],
    xaxis: {
      categories,
      tickAmount: 3, // Set X-axis categories
      labels: {
        style: {
          colors: [],
          fontSize: '12px',
          fontWeight: 500
        },
        rotate: 0
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: [],
          fontSize: '12px',
          fontWeight: 500
        }
      },
      min: 0
    },
    plotOptions: {
      bar: {
        horizontal: false, // Vertical bars
        columnWidth: '60%' // Adjust column width
      }
    },
    dataLabels: {
      enabled: false // Hide data labels on bars
    }
  };
  if (isCompare) {
    options.legend = {
      show: true,
      position: 'top',
      horizontalAlign: 'right',
      onItemClick: () => {}
    };
  }
  return options;
}

export const limitXAxisPoint = (categories, maxTick = 5) => {
  let tmpCategories = [...categories];
  const range = categories.length - 1;
  let step = 0;

  if (range % 5 === 0) {
    step = range / 5;
  } else if (range % 4 === 0) {
    step = range / 4;
  } else if (range % 3 === 0) {
    step = range / 3;
  } else if (range % 2 === 0) {
    step = range / 2;
  } else {
    step = Math.ceil(range / maxTick);
  }
  tmpCategories = categories.map((item, index) => {
    return index % step === 0 ? item : '';
  });
  return [...tmpCategories];
};

// TODO: Implement more specific metrics here
export const SEARCH_ANALYTIC_METRIC = {
  OVERVIEW: 'overview',
  NO_SEARCH_RESULT: 'no-search-result',
  SEARCH_TERMS: 'search-terms',
  SEARCH_WITH_CLICK: 'searches-with-click',
  SEARCH_WITHOUT_CLICK: 'searches-without-click',
  PRODUCT_PERFORMANCES: 'product-performances'
};

export const COLLECTION_ANALYTIC_METRIC = {
  OVERVIEW: 'overview-collection',
  COLLECTION_PERFORMANCES: 'collection-performances',
  FILTER_OPTION_VALUE: 'filter-option-value',
  FILTER_OPTION_VALUE_COMBINATION: 'filter-option-value-combination'
};

export const RECOMMENDATION_ANALYTIC_METRIC = {
  OVERVIEW: 'recommendation-overview',
  TOP_PAGE_PERFORMANCE: 'recommendation-top-page-performances',
  TOP_WIDGET_PERFORMANCE: 'recommendation-top-widget-performances',
  TOP_PRODUCT_PERFORMANCE: 'recommendation-top-product-performances'
};

export const PRE_ORDER_ANALYTIC_METRIC = {
  TOP_PRODUCT_PERFORMANCE: 'pre-order-top-product-performances'
};

export const POST_PURCHASE_ANALYTIC_METRIC = {
  TOP_WIDGET_PERFORMANCE: 'post-purchase-top-widget-performances',
  TOP_PRODUCT_PERFORMANCE: 'post-purchase-top-product-performances'
};

export const EXPORT_MODAL_TITLE_SEARCH_ANALYTICS = {
  [SEARCH_ANALYTIC_METRIC.OVERVIEW]:
    'AdminHTML.Analytics.ExportModal.title_ExportSearchAnalyticsOverview',
  [SEARCH_ANALYTIC_METRIC.SEARCH_TERMS]: 'AdminHTML.Analytics.ExportModal.title_ExportSearches',
  [SEARCH_ANALYTIC_METRIC.SEARCH_WITH_CLICK]:
    'AdminHTML.Analytics.ExportModal.title_ExportSearchesWithClick',
  [SEARCH_ANALYTIC_METRIC.SEARCH_WITHOUT_CLICK]:
    'AdminHTML.Analytics.ExportModal.title_ExportSearchesWithoutClicks',
  [SEARCH_ANALYTIC_METRIC.NO_SEARCH_RESULT]:
    'AdminHTML.Analytics.ExportModal.title_ExportSearchesWithoutResults',
  [SEARCH_ANALYTIC_METRIC.PRODUCT_PERFORMANCES]:
    'AdminHTML.Analytics.ExportModal.title_ExportProductPerformance',
  [SEARCH_ANALYTIC_METRIC.APP_IMPACT]:
    'AdminHTML.Analytics.ExportModal.title_ExportProductPerformance'
};

export const EXPORT_MODAL_TITLE_COLLECTION_ANALYTICS = {
  [COLLECTION_ANALYTIC_METRIC.OVERVIEW]:
    'AdminHTML.Analytics.ExportModal.title_ExportCollectionAnalytics_Overview',
  [COLLECTION_ANALYTIC_METRIC.COLLECTION_PERFORMANCES]:
    'AdminHTML.Analytics.ExportModal.title_ExportCollectionAnalytics_CollectionPerformance',
  [COLLECTION_ANALYTIC_METRIC.FILTER_OPTION_VALUE]:
    'AdminHTML.Analytics.ExportModal.title_ExportCollectionAnalytics_FilterOptionValue',
  [COLLECTION_ANALYTIC_METRIC.FILTER_OPTION_VALUE_COMBINATION]:
    'AdminHTML.Analytics.ExportModal.title_ExportCollectionAnalytics_FilterOptionValueCombination'
};

export const EXPORT_MODAL_TITLE_RECOMMENDATION_ANALYTICS = {
  [RECOMMENDATION_ANALYTIC_METRIC.OVERVIEW]:
    'AdminHTML.Analytics.ExportModal.title_ExportRecommendationAnalytics_Overview',
  [RECOMMENDATION_ANALYTIC_METRIC.TOP_PAGE_PERFORMANCE]:
    'AdminHTML.Analytics.ExportModal.title_ExportRecommendationAnalytics_TopPagePerformance',
  [RECOMMENDATION_ANALYTIC_METRIC.TOP_WIDGET_PERFORMANCE]:
    'AdminHTML.Analytics.ExportModal.title_ExportRecommendationAnalytics_TopWidgetPerformance',
  [RECOMMENDATION_ANALYTIC_METRIC.TOP_PRODUCT_PERFORMANCE]:
    'AdminHTML.Analytics.ExportModal.title_ExportRecommendationAnalytics_TopProductPerformance'
};

export const EXPORT_MODAL_TITLE_PRE_ORDER_ANALYTICS = {
  [PRE_ORDER_ANALYTIC_METRIC.TOP_PRODUCT_PERFORMANCE]:
    'AdminHTML.Analytics.ExportModal.title_ExportPreOrderAnalytics_TopProductPerformance'
};

export const SORT_VALUE = {
  ASCENDING: 'asc',
  DESCENDING: 'desc'
};

export const MAP_SORT_VALUE = {
  ascending: SORT_VALUE.ASCENDING,
  descending: SORT_VALUE.DESCENDING
};

export const FEATURES = {
  SEARCH: 'search',
  APP_IMPACT: 'app-impact',
  COLLECTION: 'collection',
  RECOMMENDATION: 'recommendation',
  PRE_ORDER: 'pre-order',
  POST_PURCHASE: 'post-purchase'
};

export const OPTION_DATE_PICKER = [
  'today',
  'yesterday',
  'last7Days',
  'last30Days',
  'lastMonth',
  'last90Days',
  'last180Days',
  'last365Days'
];

export const MAP_EXPORT_FILENAME = {
  [SEARCH_ANALYTIC_METRIC.OVERVIEW]: 'overview',
  [SEARCH_ANALYTIC_METRIC.SEARCH_TERMS]: 'searches',
  [SEARCH_ANALYTIC_METRIC.SEARCH_WITH_CLICK]: 'searches_with_clicks',
  [SEARCH_ANALYTIC_METRIC.SEARCH_WITHOUT_CLICK]: 'searches_without_clicks',
  [SEARCH_ANALYTIC_METRIC.NO_SEARCH_RESULT]: 'searches_without_results',
  [SEARCH_ANALYTIC_METRIC.PRODUCT_PERFORMANCES]: 'product_performance',
  [COLLECTION_ANALYTIC_METRIC.OVERVIEW]: 'collection_overview',
  [COLLECTION_ANALYTIC_METRIC.COLLECTION_PERFORMANCES]: 'collection_performance',
  [COLLECTION_ANALYTIC_METRIC.FILTER_OPTION_VALUE]: 'filter_option_value',
  [COLLECTION_ANALYTIC_METRIC.FILTER_OPTION_VALUE_COMBINATION]: 'filter_option_value_combination',
  [FEATURES.APP_IMPACT]: 'app_impact',
  [RECOMMENDATION_ANALYTIC_METRIC.OVERVIEW]: 'recommendation_analytics_overview',
  [RECOMMENDATION_ANALYTIC_METRIC.TOP_PAGE_PERFORMANCE]:
    'page_performance_in_recommendation_analytics',
  [RECOMMENDATION_ANALYTIC_METRIC.TOP_WIDGET_PERFORMANCE]:
    'widget_performance_in_recommendation_analytics',
  [RECOMMENDATION_ANALYTIC_METRIC.TOP_PRODUCT_PERFORMANCE]:
    'product_performance_in_recommendation_analytics',
  [PRE_ORDER_ANALYTIC_METRIC.TOP_PRODUCT_PERFORMANCE]: 'product_performance_in_pre_order_analytics',
  'post-purchase-top-widget-performances': 'post_purchase_widget_performance',
  'post-purchase-top-product-performances': 'post_purchase_product_performance'
};

// recommended block
export const RECOMMENDATION_RECOMMENDED_FEATURES = [
  {
    id: 'recommendation',
    name: 'AdminHTML.Analytics.RecommendedCard.recommendationBlock_Title',
    content: 'AdminHTML.Analytics.RecommendedCard.recommendationBlock_Content',
    action: 'AdminHTML.Analytics.RecommendedCard.recommendationBlock_Action',
    image: getAssetURL('assets/analytics/rec-recommended-img.png'),
    route: routes.recommendationWidgets.list()
  },
  {
    id: 'predictive-bundle',
    name: 'AdminHTML.Analytics.RecommendedCard.bundleBlock_Title',
    content: 'AdminHTML.Analytics.RecommendedCard.bundleBlock_Content',
    action: 'AdminHTML.Analytics.RecommendedCard.bundleBlock_Action',
    image: getAssetURL('assets/analytics/bundle-recommended-img.png'),
    route: routes.predictiveBundle.list()
  }
];

// recommended block
export const COLLECTION_RECOMMENDED_FEATURES = [
  {
    id: 'custom-filter-tree',
    name: 'AdminHTML.Analytics.RecommendedCard.filterTreeBlock_Title',
    content: 'AdminHTML.Analytics.RecommendedCard.filterTreeBlock_Content',
    action: 'AdminHTML.Analytics.RecommendedCard.filterTreeBlock_Action',
    image: getAssetURL('assets/analytics/filter-tree-recommended-img.png'),
    route: routes.filterTree.list()
  },
  {
    id: 'merchandising',
    name: 'AdminHTML.Analytics.RecommendedCard.merchandisingBlock_Title',
    content: 'AdminHTML.Analytics.RecommendedCard.merchandisingBlock_Content',
    action: 'AdminHTML.Analytics.RecommendedCard.merchandisingBlock_Action',
    route: routes.merchandising.view() + `?panelID=collection`,
    image: getAssetURL('assets/analytics/merchandise-recommended-img.png')
  }
];

export const MAP_PAGE_TYPES = {
  'home page': 'home-page',
  homepage: 'home-page',
  'product page': 'product-page',
  'collection page': 'collection-page',
  'cart page': 'cart-page',
  'search page': 'product-page',
  'checkout page': 'checkout-page',
};
